import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_URL } from "@/config/api";
import { getAccessToken, getOrganizationId } from "@/utils/token_functions";
import { secureAxios } from "@/middleware/encryptionMiddleware";

const postAssignRole = async (roleData) => {
  const token = getAccessToken();
  const organizationID = getOrganizationId();
  if (!organizationID) {
    throw new Error("No organization ID found");
  }

  const response = await secureAxios.post(
    `${BASE_URL}/v1/organization/assign-role`,
    {
      ...roleData,
      organizationID,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

export const createRole = createAsyncThunk(
  "role/create",
  async (roleData, { rejectWithValue }) => {
    try {
      return await postAssignRole(roleData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const updateRole = createAsyncThunk(
  "role/update",
  async (roleData, { rejectWithValue }) => {
    try {
      return await postAssignRole(roleData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Enhanced fetchRolesByAny to handle organization-specific queries
export const fetchRolesByAny = createAsyncThunk(
  "role/fetchByAny",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();

      try {
        const response = await secureAxios.get(
          `${BASE_URL}/v1/role/by-any/${key}/${value}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        return {
          data: response.data.data,
          total: response.data.data.length,
          isSingleRole: key === "_id" || key === "id",
        };
      } catch (error) {
        if (error.response?.status === 404) {
          return { data: [], total: 0, isSingleRole: false };
        }
        throw error; // rethrow for the catch below
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const deleteRole = createAsyncThunk(
  "role/delete",
  async (roleIds, { rejectWithValue }) => {
    try {
      const token = getAccessToken();

      const response = await secureAxios.delete(`${BASE_URL}/v1/role`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: roleIds, // Maintain the array format
      });

      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        // You could handle 404 specifically if needed
        return { success: false, message: "Roles not found" };
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
      hasEncryptedPayload: true,
      requiresDecryption: true,
    },
  }
);

// Enhanced fetchAllRoles with better error handling for offline scenarios
export const fetchAllRoles = createAsyncThunk(
  "role/fetchAll",
  async ({ skip = 0, limit = 10 }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const organizationID = getOrganizationId();

      if (!organizationID) {
        throw new Error("No organization ID found");
      }

      try {
        const response = await secureAxios.get(
          `${BASE_URL}/v1/organization/role?skip=${skip}&limit=${limit}&organizationID=${organizationID}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        return {
          data: response.data.data || [],
          total: response.data.data?.length || 0,
        };
      } catch (error) {
        if (error.response?.status === 404) {
          return { data: [], total: 0 };
        }
        throw error;
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const fetchSingleRole = createAsyncThunk(
  "role/fetchSingle",
  async ({ id }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await secureAxios.get(
        `${BASE_URL}/v1/organization/role/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const roleData = response.data.data;
      const rawUsers = Array.isArray(roleData?.users) ? roleData.users : [];

      if (rawUsers.length === 0) {
        return roleData;
      }

      const flattened = rawUsers.flatMap((entry) =>
        Array.isArray(entry) ? entry : [entry]
      );

      const populatedUsers = flattened.filter(
        (user) =>
          user &&
          typeof user === "object" &&
          (user.fullname || user.email || user.username || user.profileImage)
      );

      if (populatedUsers.length === flattened.length) {
        return { ...roleData, users: populatedUsers };
      }

      const userIds = [
        ...new Set(
          flattened
            .map((user) =>
              typeof user === "string" ? user : user?._id || user?.id
            )
            .filter(Boolean)
        ),
      ];

      if (userIds.length === 0) {
        return { ...roleData, users: populatedUsers };
      }

      const organizationID =
        (typeof roleData.organizationID === "string"
          ? roleData.organizationID
          : roleData.organizationID?._id) || getOrganizationId();

      if (!organizationID) {
        return {
          ...roleData,
          users: populatedUsers.length > 0 ? populatedUsers : flattened,
        };
      }

      const orgUsersResponse = await secureAxios.get(
        `${BASE_URL}/v1/user/by-any/organizationID/${organizationID}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const orgUsersRaw = orgUsersResponse.data?.data;
      const orgUsers = Array.isArray(orgUsersRaw?.result)
        ? orgUsersRaw.result
        : Array.isArray(orgUsersRaw)
          ? orgUsersRaw
          : [];

      const orgUserById = new Map(
        orgUsers.map((user) => [user._id || user.id, user])
      );

      const users = userIds
        .map((userId) => orgUserById.get(userId))
        .filter(Boolean);

      if (users.length === 0 && populatedUsers.length > 0) {
        return { ...roleData, users: populatedUsers };
      }

      return {
        ...roleData,
        users,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const assignRoleUsersAsync = createAsyncThunk(
  "role/assignUsers",
  async (roleData, { rejectWithValue, dispatch }) => {
    try {
      const response = await postAssignRole(roleData);

      if (roleData.id) {
        await dispatch(fetchSingleRole({ id: roleData.id })).unwrap();
      }

      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const deleteSingleRole = createAsyncThunk(
  "role/deleteSingle",
  async (payload, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.delete(
        `${BASE_URL}/v1/organization/role`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          data: [payload.id], // Send as array of strings
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

const initialState = {
  roles: [],
  fetchedRoles: [],
  currentRole: null,
  currentStep: 1,
  isLoading: false,
  error: null,
  success: false,
  total: 0,
  formData: {
    roleId: "",
    isEditMode: false,
    roleName: "",
    description: "",
    userIDs: [],
    permissions: [],
    organizationID: "",
  },
  singleRole: null,
  singleRoleLoading: false,
  singleRoleError: null,
  isOfflineData: false,
};

const roleSlice = createSlice({
  name: "role",
  initialState,
  reducers: {
    resetRoleState: (state) => {
      return {
        ...initialState,
        roles: state.roles,
        total: state.total,
      };
    },
    resetRoleFormData: (state) => {
      state.currentStep = 1;
      state.formData = {
        roleId: "",
        isEditMode: false,
        roleName: "",
        description: "",
        userIDs: [],
        permissions: [],
        organizationID: "",
      };
      state.currentRole = null;
    },
    updateRoleForm: (state, action) => {
      state.currentRole = {
        ...state.currentRole,
        ...action.payload,
      };
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    updateStep: (state, action) => {
      const { data } = action.payload;
      state.formData = {
        ...state.formData,
        permissions: data.privileges,
      };
    },
    updateRoleFormData: (state, action) => {
      state.formData = {
        ...state.formData,
        ...action.payload,
      };
    },
    setRoleFormForEdit: (state, action) => {
      const payload = action.payload || {};
      state.currentStep = 1;
      state.formData = {
        roleId: payload.roleId || payload._id || payload.id || "",
        isEditMode: true,
        roleName: payload.roleName || payload.name || "",
        description: payload.description || "",
        userIDs: Array.isArray(payload.userIDs)
          ? payload.userIDs
          : Array.isArray(payload.users)
            ? payload.users
                .flatMap((u) => (Array.isArray(u) ? u : [u]))
                .map((u) => (typeof u === "string" ? u : u?._id || u?.id))
                .filter(Boolean)
            : [],
        permissions: Array.isArray(payload.permissions) ? payload.permissions : [],
        organizationID: payload.organizationID || "",
      };
    },
    addPermission: (state, action) => {
      if (!state.formData.permissions.includes(action.payload)) {
        state.formData.permissions.push(action.payload);
      }
    },
    removePermission: (state, action) => {
      state.formData.permissions = state.formData.permissions.filter(
        (permission) => permission !== action.payload
      );
    },
    setUserIDs: (state, action) => {
      state.formData.userIDs = action.payload;
    },
    loadCachedData: (state, action) => {
      state.roles = action.payload.roles || [];
      state.fetchedRoles = action.payload.fetchedRoles || [];
      state.total = action.payload.total || 0;
      state.isOfflineData = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createRole.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
        state.currentStep = 1;
        state.formData = {
          roleName: "",
          description: "",
          userIDs: [],
          permissions: [],
          organizationID: "",
        };
        state.currentRole = null;
      })
      .addCase(createRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchRolesByAny.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRolesByAny.fulfilled, (state, action) => {
        state.isLoading = false;
        state.fetchedRoles = action.payload.data;

        if (action.payload.isSingleRole && action.payload.data.length > 0) {
          state.currentRole = action.payload.data[0];
        }

        if (!action.payload.isSingleRole) {
          state.total = action.payload.total;
        }

        state.error = null;
        state.isOfflineData = false;
      })
      .addCase(fetchRolesByAny.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        const seen = new Set();
        const deduped = (action.payload.data || []).filter((role) => {
          const roleId = role._id || role.id;
          if (!roleId || seen.has(roleId)) return false;
          seen.add(roleId);
          return true;
        });
        state.roles = deduped;
        state.total = deduped.length;
        state.error = null;
        state.isOfflineData = false;
      })
      .addCase(fetchAllRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchSingleRole.pending, (state) => {
        state.singleRoleLoading = true;
        state.singleRoleError = null;
      })
      .addCase(fetchSingleRole.fulfilled, (state, action) => {
        state.singleRoleLoading = false;
        state.singleRole = action.payload;
        state.singleRoleError = null;
      })
      .addCase(fetchSingleRole.rejected, (state, action) => {
        state.singleRoleLoading = false;
        state.singleRoleError = action.payload;
      })
      .addCase(deleteSingleRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSingleRole.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(deleteSingleRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // assignRoleUsersAsync
      .addCase(assignRoleUsersAsync.pending, (state) => {
        state.isLoading = true;
        state.singleRoleLoading = true;
        state.error = null;
      })
      .addCase(assignRoleUsersAsync.fulfilled, (state) => {
        state.isLoading = false;
        state.singleRoleLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(assignRoleUsersAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.singleRoleLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  resetRoleState,
  resetRoleFormData,
  updateRoleForm,
  setCurrentStep,
  updateStep,
  updateRoleFormData,
  setRoleFormForEdit,
  addPermission,
  removePermission,
  setUserIDs,
  loadCachedData,
} = roleSlice.actions;

// Selectors
export const selectRoles = (state) => state.role.roles;
export const selectFetchedRoles = (state) => state.role.fetchedRoles || [];
export const selectCurrentRole = (state) => state.role.currentRole;
export const selectRoleLoading = (state) => state.role.isLoading;
export const selectRoleError = (state) => state.role.error;
export const selectRoleSuccess = (state) => state.role.success;
export const selectRoleTotal = (state) => state.role.total;
export const selectCurrentStep = (state) => state.role.currentStep;
export const selectRoleFormData = (state) => state.role.formData;
export const selectSingleRole = (state) => state.role.singleRole;
export const selectSingleRoleLoading = (state) => state.role.singleRoleLoading;
export const selectSingleRoleError = (state) => state.role.singleRoleError;
export const selectIsOfflineData = (state) => state.role.isOfflineData;

export default roleSlice.reducer;
